import { useEffect, useMemo } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { EditSubCategory } from "../../../types";

const LANGUAGES = [
	{ key: "en-us", label: "English (US)" },
	{ key: "de-ch", label: "Deutsch (CH)" },
	{ key: "de-de", label: "Deutsch (DE)" },
	{ key: "fr-fr", label: "Français" },
	{ key: "it-it", label: "Italiano" },
] as const;

const schema = z.object({
	categoryId: z.string().min(1, "Category is required"),
	itemCategoryId: z.string().min(1, "Subcategory is required"),
	"en-us": z.string().min(1, "English is required"),
	"de-ch": z.string().default(""),
	"de-de": z.string().default(""),
	"fr-fr": z.string().default(""),
	"it-it": z.string().default(""),
	weight: z.coerce.number().min(0, "Weight must be 0 or more"),
	isTextbox: z.boolean(),
	sequence: z.coerce.number().min(0, "Sequence must be 0 or more"),
	isPublicRequired: z.boolean(),
	isBackofficeRequired: z.boolean(),
});

export type QuestionFormData = z.infer<typeof schema>;

interface Props {
	show: boolean;
	onHide: () => void;
	onSubmit: (data: QuestionFormData) => void;
	title: string;
	defaultValues?: Partial<QuestionFormData>;
	editCategories: EditSubCategory[];
	isEditMode?: boolean;
}

export default function QuestionFormModal({ show, onHide, onSubmit, title, defaultValues, editCategories, isEditMode = false }: Props) {
	const {
		register,
		handleSubmit,
		reset,
		watch,
		control,
		formState: { errors },
	} = useForm<QuestionFormData>({
		resolver: zodResolver(schema) as any,
		defaultValues: {
			categoryId: "",
			itemCategoryId: "",
			"en-us": "",
			"de-ch": "",
			"de-de": "",
			"fr-fr": "",
			"it-it": "",
			weight: 1000,
			isTextbox: false,
			sequence: 10,
			isPublicRequired: false,
			isBackofficeRequired: false,
			...defaultValues,
		},
	});

	const selectedCategoryId = watch("categoryId");

	const { categories, subcategories } = useMemo(() => {
		const parentIdSet = new Set<string>();
		editCategories
			.filter((f) => f.IsDeleted == false)
			.forEach((item) => {
				if (item.ItemCategory_fk) {
					parentIdSet.add(item.ItemCategory_fk);
				}
			});
 
		const categories: { id: string; name: string }[] = [];
		const subcategories: { id: string; parentId: string; name: string }[] = [];

		editCategories
			.filter((f) => f.IsDeleted == false)
			.forEach((item) => {
				if (parentIdSet.has(item.ItemCategory_ID) || !item.ItemCategory_fk) {
					categories.push({
						id: item.ItemCategory_ID,
						name: item["en-us"] || item["de-ch"] || item["de-de"] || item.ItemCategory_ID,
					});
				} else {
					subcategories.push({
						id: item.ItemCategory_ID,
						parentId: item.ItemCategory_fk,
						name: item["en-us"] || item["de-ch"] || item["de-de"] || item.ItemCategory_ID,
					});
				}
			});

		return { categories: categories.sort((a, b) => a.name.localeCompare(b.name)), subcategories };
	}, [editCategories]);

	const filteredSubcategories = useMemo(() => {
		if (!selectedCategoryId) return [];
		return subcategories
			.filter((sub) => sub.parentId === selectedCategoryId)
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [subcategories, selectedCategoryId]);

	useEffect(() => {
		if (show) {
			reset({
				categoryId: "",
				itemCategoryId: "",
				"en-us": "",
				"de-ch": "",
				"de-de": "",
				"fr-fr": "",
				"it-it": "",
				weight: 1000,
				isTextbox: false,
				sequence: 10,
				isPublicRequired: false,
				isBackofficeRequired: false,
				...defaultValues,
			});
		}
	}, [show, defaultValues, reset]);

	const handleFormSubmit = (data: QuestionFormData) => {
		onSubmit(data); 
		onHide();
	};

	return (
		<Modal show={show} onHide={onHide} centered size="lg">
			<Modal.Header closeButton>
				<Modal.Title className="text-truncate" style={{ maxWidth: "90%" }}>
					{title}
				</Modal.Title>
			</Modal.Header>
			<form onSubmit={handleSubmit(handleFormSubmit)}>
				<Modal.Body>
					<div className="row mb-3">
						<div className="col-md-6">
							<Form.Group>
								<Form.Label>
									Category <span className="text-danger">*</span>
								</Form.Label>
								<Form.Select isInvalid={!!errors.categoryId} {...register("categoryId")} disabled={isEditMode}>
									<option value="">Select category...</option>
									{categories.map((cat) => (
										<option key={cat.id} value={cat.id}>
											{cat.name}
										</option>
									))}
								</Form.Select>
								{errors.categoryId && (
									<Form.Control.Feedback type="invalid">
										{errors.categoryId.message}
									</Form.Control.Feedback>
								)}
							</Form.Group>
						</div>
						<div className="col-md-6">
							<Form.Group>
								<Form.Label>
									Subcategory <span className="text-danger">*</span>
								</Form.Label>
								<Form.Select
									isInvalid={!!errors.itemCategoryId}
									{...register("itemCategoryId")}
									disabled={!selectedCategoryId || isEditMode}
								>
									<option value="">Select subcategory...</option>
									{filteredSubcategories.map((sub) => (
										<option key={sub.id} value={sub.id}>
											{sub.name}
										</option>
									))}
								</Form.Select>
								{errors.itemCategoryId && (
									<Form.Control.Feedback type="invalid">
										{errors.itemCategoryId.message}
									</Form.Control.Feedback>
								)}
							</Form.Group>
						</div>
					</div>

					<hr />
					<h6 className="mb-3">Question translations</h6>

					{LANGUAGES.map((lang) => (
						<Form.Group className="mb-3" key={lang.key}>
							<Form.Label>
								{lang.label}
								{lang.key === "en-us" && <span className="text-danger ms-1">*</span>}
							</Form.Label>
							<Form.Control
								type="text"
								placeholder={lang.label}
								isInvalid={!!errors[lang.key]}
								{...register(lang.key)}
							/>
							{errors[lang.key] && (
								<Form.Control.Feedback type="invalid">
									{errors[lang.key]?.message}
								</Form.Control.Feedback>
							)}
						</Form.Group>
					))}

					<hr />
					<h6 className="mb-3">Settings</h6>

					<div className="row mb-3">
						<div className="col-md-4">
							<Form.Group>
								<Form.Label>Weight</Form.Label>
								<Form.Control type="number" isInvalid={!!errors.weight} {...register("weight")} />
								{errors.weight && (
									<Form.Control.Feedback type="invalid">
										{errors.weight.message}
									</Form.Control.Feedback>
								)}
							</Form.Group>
						</div>
						<div className="col-md-4">
							<Form.Group>
								<Form.Label>Sequence</Form.Label>
								<Form.Control type="number" isInvalid={!!errors.sequence} {...register("sequence")} />
								{errors.sequence && (
									<Form.Control.Feedback type="invalid">
										{errors.sequence.message}
									</Form.Control.Feedback>
								)}
							</Form.Group>
						</div>
					</div>

					<div className="d-flex gap-4">
						<Controller
							name="isTextbox"
							control={control}
							render={({ field }) => (
								<Form.Check
									type="checkbox"
									label="Is Textbox"
									checked={field.value}
									onChange={(e) => field.onChange(e.target.checked)}
								/>
							)}
						/>
						<Controller
							name="isPublicRequired"
							control={control}
							render={({ field }) => (
								<Form.Check
									type="checkbox"
									label="Public Required"
									checked={field.value}
									onChange={(e) => field.onChange(e.target.checked)}
								/>
							)}
						/>
						<Controller
							name="isBackofficeRequired"
							control={control}
							render={({ field }) => (
								<Form.Check
									type="checkbox"
									label="Backoffice Required"
									checked={field.value}
									onChange={(e) => field.onChange(e.target.checked)}
								/>
							)}
						/>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<Button variant="outline-secondary" onClick={onHide}>
						Cancel
					</Button>
					<Button variant="primary" type="submit">
						Save
					</Button>
				</Modal.Footer>
			</form>
		</Modal>
	);
}
